import { useState } from "react";
import { Flex, Spinner, Text, VStack, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Contato,
  IAtualizarEnderecoRequest,
  ICriarContatoRequest,
  IParteContatoCobrancaRetorno,
  ParteContatoCobranca,
} from "@/models/consignado/proposal-billings.model";
import {
  adicionarContatoCobranca,
  atualizarContatoCobranca,
  atualizarEnderecoCobranca,
  deletarContatoCobranca,
} from "@/services/consignado/proposta-cobranca.service";
import { removeMask } from "@/utils/removeMask";
import ConfirmDialog from "../components/ConfirmDialog";
import CollapsibleSection from "./CollapsibleSection";
import ContatoFormModal, { ContatoFormValues } from "./ContatoFormModal";
import EnderecoFormModal from "./EnderecoFormModal";
import ParteContatoCard from "./ParteContatoCard";
import { useApiErrorToast } from "@/hooks/useApiErrorToast";
import { CONTATOS_CCB_QUERY_KEY, useContatosPorCcb } from "@/hooks/useContatosPorCcb";

type Props = {
  numeroCcb: string;
};

// Seção "Dados de Contato" do detalhe da CCB — REAL (HIS-024 / TAR-049,
// backend his-adm-cobranca-020 `efab7a1f`). Uma query só
// (GET /proposta/cobrancas/:ccb/contatos) traz tomador e empregador com nome,
// documento, endereço e contatos já separados por tipo. Nasce colapsada e só
// dispara a query na 1ª abertura (mesmo lazy-load das outras seções).
//
// Escritas (todas REAIS; depois de cada uma a query é invalidada):
//  - adicionar contato: POST /contatos, com `ref_id` = id do tomador /
//    empregador que a própria rota devolveu e `tipo` TOMADOR | EMPREGADOR.
//  - editar contato: PUT /contatos/:id (nome, telefone, e-mail, temperatura).
//  - excluir contato: DELETE /contatos/:id, com confirmação.
//  - endereço: PUT /cobrancas/:ccb/contatos/:parte/endereco — cria se a parte
//    ainda não tiver endereço (empregador é o caso comum, relação nova).
const TIPO_POR_PARTE: Record<ParteContatoCobranca, string> = {
  tomador: "TOMADOR",
  empregador: "EMPREGADOR",
};

type ContatoModalState = { parte: ParteContatoCobranca; contato: Contato | null };

export default function ContatosSection({ numeroCcb }: Props) {
  const toast = useToast();
  const toastErro = useApiErrorToast();
  const queryClient = useQueryClient();
  const [aberta, setAberta] = useState(false);
  const [contatoModal, setContatoModal] = useState<ContatoModalState | null>(null);
  const [enderecoModal, setEnderecoModal] = useState<ParteContatoCobranca | null>(null);
  const [contatoParaExcluir, setContatoParaExcluir] = useState<Contato | null>(null);

  const { data, isLoading, isError } = useContatosPorCcb(numeroCcb, aberta);

  const invalidar = () =>
    queryClient.invalidateQueries({ queryKey: [CONTATOS_CCB_QUERY_KEY, numeroCcb] });

  const salvarContato = useMutation({
    mutationFn: ({
      parte,
      contato,
      values,
    }: ContatoModalState & { values: ContatoFormValues }) => {
      const campos = {
        nome: parte === "empregador" ? values.nome.trim() : undefined,
        telefone: removeMask(values.telefone),
        email: values.email.trim(),
        temperatura: values.temperatura,
      };
      if (contato) return atualizarContatoCobranca(contato.id, campos);
      const refId = data?.[parte].id;
      if (!refId) return Promise.reject(new Error("Parte sem id: não é possível adicionar contato."));
      const novo: ICriarContatoRequest = { ...campos, ref_id: refId, tipo: TIPO_POR_PARTE[parte] };
      return adicionarContatoCobranca(novo);
    },
    onSuccess(_, { contato }) {
      toast({
        title: contato ? "Contato atualizado" : "Contato adicionado",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setContatoModal(null);
      invalidar();
    },
    onError: toastErro("Erro ao salvar contato"),
  });

  const excluirContato = useMutation({
    mutationFn: (contato: Contato) => deletarContatoCobranca(contato.id),
    onSuccess() {
      setContatoParaExcluir(null);
      invalidar();
    },
    onError: toastErro("Erro ao excluir contato"),
  });

  const salvarEndereco = useMutation({
    mutationFn: ({ parte, values }: { parte: ParteContatoCobranca; values: IAtualizarEnderecoRequest }) =>
      atualizarEnderecoCobranca(numeroCcb, parte, values),
    onSuccess() {
      toast({ title: "Endereço salvo", status: "success", duration: 3000, isClosable: true });
      setEnderecoModal(null);
      invalidar();
    },
    onError: toastErro("Erro ao salvar endereço"),
  });

  const renderParte = (parte: ParteContatoCobranca, dados: IParteContatoCobrancaRetorno) => (
    <ParteContatoCard
      key={parte}
      parte={parte}
      dados={dados}
      onAdicionar={() => setContatoModal({ parte, contato: null })}
      onEditar={(contato) => setContatoModal({ parte, contato })}
      onExcluir={setContatoParaExcluir}
      onEditarEndereco={() => setEnderecoModal(parte)}
      excluindoId={excluirContato.isPending ? excluirContato.variables?.id : null}
    />
  );

  const conteudo = isLoading ? (
    <Flex justifyContent="center" py={4}>
      <Spinner color="primary" />
    </Flex>
  ) : isError || !data ? (
    <Text fontSize="sm" color="gray.500">
      Não foi possível carregar os dados de contato desta CCB.
    </Text>
  ) : (
    <VStack spacing={6} align="stretch">
      {renderParte("tomador", data.tomador)}
      {renderParte("empregador", data.empregador)}
    </VStack>
  );

  return (
    <>
      <CollapsibleSection
        title="Dados de Contato"
        defaultExpanded={false}
        onToggle={(expanded) => expanded && setAberta(true)}
      >
        {aberta && conteudo}
      </CollapsibleSection>

      {contatoModal && (
        <ContatoFormModal
          isOpen
          onClose={() => setContatoModal(null)}
          parte={contatoModal.parte}
          contato={contatoModal.contato}
          // Erro já vira toast no onError; o catch evita rejeição sem handler.
          onSubmit={(values) =>
            salvarContato.mutateAsync({ ...contatoModal, values }).catch(() => undefined)
          }
          isSaving={salvarContato.isPending}
        />
      )}

      <ConfirmDialog
        isOpen={!!contatoParaExcluir}
        onClose={() => setContatoParaExcluir(null)}
        onConfirm={() => contatoParaExcluir && excluirContato.mutate(contatoParaExcluir)}
        titulo="Excluir contato"
        descricao={
          <>
            Excluir o contato{contatoParaExcluir?.nome ? ` ${contatoParaExcluir.nome}` : ""}
            {contatoParaExcluir?.email ? ` (${contatoParaExcluir.email})` : ""}? Essa ação não pode
            ser desfeita.
          </>
        }
        confirmLabel="Excluir"
        isLoading={excluirContato.isPending}
      />

      {enderecoModal && data && (
        <EnderecoFormModal
          isOpen
          onClose={() => setEnderecoModal(null)}
          rotuloParte={enderecoModal}
          endereco={data[enderecoModal].endereco}
          onSubmit={(values) =>
            salvarEndereco.mutateAsync({ parte: enderecoModal, values }).catch(() => undefined)
          }
          isSaving={salvarEndereco.isPending}
        />
      )}
    </>
  );
}
